<template>
	<div class="tooltip-content">
		<div
		class="percentage">
			{{`${tooltipInfo.parsedPercentage === 0 && tooltipInfo.percentage !== 0 ? '≈ ' : ''}${tooltipInfo.parsedPercentage}%`}}
		</div>
		<div class="description-color-wrapper">
			<div class="color-square" :style="{backgroundColor: colors[labelHoverIndex]}"></div>
			<div class="item-label" v-if="tooltipInfo.description">{{tooltipInfo.description}}</div>
			<!-- Account Description -->
			<div
			v-if="!tooltipInfo.description"
			v-mask="tooltipInfo.accountDescription ? null : (tooltipInfo.accountType !== 'DWALLET' ? ACCOUNTS_NIB_IBAN : WALLET)"
			class="item-label">
				{{tooltipInfo.accountDescription ? tooltipInfo.accountDescription : tooltipInfo.accountNumber}}
			</div>
			<!-- Account Number -->
			<div
			v-if="tooltipInfo.accountDescription"
			v-mask="tooltipInfo.accountType !== 'DWALLET' ? ACCOUNTS_NIB_IBAN : WALLET"
			class="account-number">
				{{tooltipInfo.accountNumber}}
			</div>
		</div>
		<div class="amounts-wrapper">
			<div
			v-for="amount in allCurrenciesAmounts"
			:key="`${amount.currency}${amount.totalAmount}`"
			class="amount-sign-wrapper">
				<!-- Icon -->
				<component :is="amountIcon(amount.totalAmount)" class="icon" :class="{'negative': amount.totalAmount < 0}"/>
				<!-- Amount -->
				<div class="amount">{{amountFormat(Math.abs(amount.totalAmount), amount.currency)}}</div>
			</div>
		</div>
	</div>
</template>

<script setup>
/* Vue & Helpers */
import { computed, defineAsyncComponent } from 'vue'
import { amountFormat } from '@/assets/scripts/amounts'
import { ACCOUNTS_NIB_IBAN, WALLET } from '@/assets/constants/masks'
/* Store */
import { mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/** props received
@colors {Array} - array of colors from our graph/labels
@labelHoverIndex {Number} - hover index
@tooltipInfo {Object} - info to display in tooltip
*/
const props = defineProps({
	colors: Array,
	labelHoverIndex: Number,
	tooltipInfo: Object
})

const { getCurrenciesList } = mapGetters(moduleMapper.HOME)
/*
* When our chart is made of products we need to display balances separated by currency
*/
const allCurrenciesAmounts = computed(() => {
	const currencyAmounts = []
	const allCurrencies = getCurrenciesList.value

	allCurrencies.forEach(currency => {
		if (props.tooltipInfo.accounts.filter(account => account.accountCurrency === currency.name).length) {
			let currencyAmount = 0

			props.tooltipInfo.accounts.filter(account => account.accountCurrency === currency.name).forEach(account => {
				currencyAmount += account.balance || 0
			})

			currencyAmounts.push({
				currency: currency.name,
				totalAmount: currencyAmount,
				numericClass: currencyAmount === 0 ? '' : (currencyAmount > 0 ? 'plus-symbol' : 'minus-symbol')
			})
		}
	})
	return currencyAmounts
})

const amountIcon = amount => {
	if (!amount || amount === 0) return null
	const icon = amount > 0 ? 'plus' : 'minus'
	return defineAsyncComponent(() => import('@/assets/icons/' + icon + '.svg?inline'))
}
</script>

<style lang="scss" scoped>
.tooltip-content {
	min-width: 166px;
	height: auto;
	padding: 16px;
	box-sizing: border-box;

	background-color: var(--secondary-light-4);
	border-radius: 8px;
	box-shadow: var(--box-shadow-2);

	.percentage {
		@include H2-Regular;
		line-height: 36px;

		color: var(--secondary-dark-4);
	}

	.description-color-wrapper {
		width: 100%;
		margin-bottom: 16px;

		display: flex;
		flex-direction: row;
		align-items: center;
		flex-wrap: wrap;

		.color-square {
			width: 16px;
			height: 16px;

			flex-shrink: 0;

			margin-right: 8px;
			border-radius: 50%;
		}

		.item-label {
			width: calc(100% - 24px);
			height: 16px;
			line-height: 16px;

			@include H7-Regular;
			line-height: 16px;
			color: var(--secondary-dark-4);
			white-space: nowrap;
			text-overflow: ellipsis;
			overflow: hidden;
		}

		.account-number {
			width: calc(100% - 24px);
			margin-left: 24px;

			font-size: 14px;
			font-weight: 500;
			color: var(--secondary-variant-1);
		}
	}

	.amounts-wrapper {
		width: 100%;
		height: auto;

		.amount-sign-wrapper {
			width: 100%;
			height: 20px;

			display: flex;
			align-items: center;

			@include H8-Regular;
			line-height: 13px;
			color: var(--secondary-dark-1);

			.icon {
				width: 8px;
				height: 8px;

				margin-right: 5px;

				:deep(.change-color-fill) {
					stroke: var(--positive);
				}

				&.negative {
					:deep(.change-color-fill) {
						stroke: var(--negative);
					}
				}
			}
		}
	}
}
</style>
